/**
 * The v2 easing engine: one pure function from (easing, linear progress) to
 * eased progress: ten named curves × an intensity dial.
 *
 * The bezier family delegates to Remotion's solver with control points
 * interpolated by intensity; the expressive family (overshoot, elastic,
 * bounce, impulse, swing) is closed-form — we sample per frame, so springs
 * and oscillations need no keyframe baking the way Lottie does.
 *
 * The exact numbers below are frozen by snapshot tests. Overshoot and
 * impulse are ground-truthed against the keyframe bakes in Jitter reference
 * exports; the rest are taste. Once ads are saved against these curves, the
 * values are semantics.
 *
 * The `Easing` shape itself is part of the block data model in
 * `@hightouch/lib` (the editor stores it in markup); it is re-exported here.
 */
import type {
  Easing,
  EasingName,
} from "@hightouch/lib/ad-studio/html-editor/animation-blocks";
import { Easing as RemotionEasing } from "remotion";

export { EASING_NAMES } from "@hightouch/lib/ad-studio/html-editor/animation-blocks";
export type { Easing, EasingName };

export const DEFAULT_EASING: Easing = { name: "smooth" };

type Bezier = [number, number, number, number];

/** Control points at intensity 0 and 100; intermediate intensities lerp. */
const BEZIER_ANCHORS: Partial<Record<EasingName, [Bezier, Bezier]>> = {
  // Symmetric ease-in-out; higher intensity holds longer at both ends.
  smooth: [
    [0.35, 0, 0.65, 1],
    [0.7, 0, 0.3, 1],
  ],
  // Asymmetric, deceleration-biased — the "CSS ease" character.
  natural: [
    [0.25, 0.1, 0.25, 1],
    [0.4, 0.15, 0.1, 1],
  ],
  // Ease-out; higher intensity approaches an expo-style hard landing.
  "slow-down": [
    [0, 0, 0.58, 1],
    [0.05, 0.9, 0.1, 1],
  ],
  // Mirror of slow-down: ease-in toward an expo-style launch.
  accelerate: [
    [0.42, 0, 1, 1],
    [0.9, 0, 0.95, 0.1],
  ],
};

export const lerp = (a: number, b: number, t: number): number =>
  a + (b - a) * t;

export const clamp01 = (value: number): number =>
  Math.min(1, Math.max(0, value));

const lerpBezier = (from: Bezier, to: Bezier, t: number): Bezier => [
  lerp(from[0], to[0], t),
  lerp(from[1], to[1], t),
  lerp(from[2], to[2], t),
  lerp(from[3], to[3], t),
];

/** Normalized intensity in [0, 1]; malformed values fall back to the default. */
function intensityOf(easing: Easing): number {
  const { intensity } = easing;
  if (typeof intensity !== "number" || !Number.isFinite(intensity)) return 0.5;
  return Math.min(100, Math.max(0, intensity)) / 100;
}

/**
 * Eased progress for linear progress `t` (clamped to [0, 1]). The expressive
 * curves intentionally exceed 1 mid-flight (overshoot/elastic/impulse) or dip
 * below 0 (swing's anticipation) — callers interpolate values with the result
 * rather than clamping it.
 */
export function easedProgress(easing: Easing | undefined, t: number): number {
  const resolved = easing ?? DEFAULT_EASING;
  const x = clamp01(t);
  if (x === 0) return 0;
  if (x === 1) return 1;

  if (resolved.bezier) {
    return RemotionEasing.bezier(...resolved.bezier)(x);
  }

  const i = intensityOf(resolved);
  const anchors = BEZIER_ANCHORS[resolved.name];
  if (anchors) {
    return RemotionEasing.bezier(...lerpBezier(anchors[0], anchors[1], i))(x);
  }

  switch (resolved.name) {
    case "linear":
      return x;
    case "overshoot":
      return overshoot(x, i);
    case "elastic":
      return elastic(x, i);
    case "bounce":
      return bounce(x, i);
    case "impulse":
      return impulse(x, i);
    case "swing":
      return swing(x, i);
    default:
      // Unrecognized name from untrusted JSON — degrade to linear rather
      // than throwing mid-render.
      return x;
  }
}

/**
 * Pop past the target early, then spend most of the run settling back.
 * Shape ground-truthed against Jitter's Lottie exports (their Overshoot
 * bakes to a peak at ~30% of the duration with a long smooth settle —
 * easeOutBack peaks far too late at ~80%). Intensity maps to the peak
 * amplitude; 50 gives the reference ads' 120% pop.
 */
const OVERSHOOT_PEAK_AT = 0.3;

function overshoot(x: number, i: number): number {
  const amplitude = lerp(0.05, 0.35, i);
  if (x < OVERSHOOT_PEAK_AT) {
    // Rise 0 → 1+a, ease-out (the bake's segment is ~(0, 0, 0.2, 1)).
    return (1 + amplitude) * (1 - (1 - x / OVERSHOOT_PEAK_AT) ** 3);
  }
  // Settle 1+a → 1 along a smoothstep (the bake's ~(0.2, 0, 0.25, 1)).
  const q = (x - OVERSHOOT_PEAK_AT) / (1 - OVERSHOOT_PEAK_AT);
  return 1 + amplitude * (1 - q * q * (3 - 2 * q));
}

/** easeOutElastic: exponentially decaying oscillation around the target. */
function elastic(x: number, i: number): number {
  // Smaller period = more visible oscillations before settling.
  const period = lerp(0.6, 0.25, i);
  return (
    2 ** (-10 * x) * Math.sin(((x - period / 4) * (2 * Math.PI)) / period) + 1
  );
}

/** The classic easeOutBounce piecewise curve (easings.net constants). */
function standardBounce(x: number): number {
  const n1 = 7.5625;
  const d1 = 2.75;
  if (x < 1 / d1) return n1 * x * x;
  if (x < 2 / d1) {
    const y = x - 1.5 / d1;
    return n1 * y * y + 0.75;
  }
  if (x < 2.5 / d1) {
    const y = x - 2.25 / d1;
    return n1 * y * y + 0.9375;
  }
  const y = x - 2.625 / d1;
  return n1 * y * y + 0.984375;
}

/**
 * Bounce with intensity as rebound prominence.
 * ponytail: blends the fixed-constant classic bounce with a plain ease-out
 * rather than simulating restitution; swap in a parametric bounce if the
 * blend reads as mushy at low intensities.
 */
function bounce(x: number, i: number): number {
  const weight = lerp(0.35, 1, i);
  const easeOut = 1 - (1 - x) ** 2;
  return lerp(easeOut, standardBounce(x), weight);
}

/**
 * Anticipation, then a whip: drift opposite the travel direction for most
 * of the run, then snap to the target at the very end — Jitter's Impulse
 * ("moves the object opposite to the main direction first"). Ground-truthed
 * against their exports: the dip peaks at ~58% of the duration and the
 * finish is an extreme ease-in (~(0.96, 0, 0.99, 0.7)). Intensity maps to
 * the dip amplitude; 70 gives the reference ads' swell-then-collapse exit
 * (scale 1 → 1.38 → 0).
 */
const IMPULSE_DIP_AT = 0.58;

function impulse(x: number, i: number): number {
  const amplitude = lerp(0.1, 0.5, i);
  if (x < IMPULSE_DIP_AT) {
    // Drift 0 → -a along a smoothstep (the bake is near-linear).
    const q = x / IMPULSE_DIP_AT;
    return -amplitude * q * q * (3 - 2 * q);
  }
  // Hold near the dip, then whip to the target in the final frames.
  const q = (x - IMPULSE_DIP_AT) / (1 - IMPULSE_DIP_AT);
  return -amplitude + (1 + amplitude) * q ** 5;
}

/** easeInOutBack: anticipates below 0, then settles from beyond 1. */
function swing(x: number, i: number): number {
  const c1 = lerp(0.4, 2.5, i);
  const c2 = c1 * 1.525;
  return x < 0.5
    ? ((2 * x) ** 2 * ((c2 + 1) * 2 * x - c2)) / 2
    : ((2 * x - 2) ** 2 * ((c2 + 1) * (2 * x - 2) + c2) + 2) / 2;
}
